const problems = [
  {
    icon: 'ri-phone-line',
    title: 'Missed Calls During Jobs',
    desc: 'Every unanswered call is a competitor\'s opportunity. Your team can\'t be everywhere at once.',
    stat: '62%',
    statLabel: 'of callers won\'t call back',
  },
  {
    icon: 'ri-time-line',
    title: 'Slow Response to Inquiries',
    desc: 'Leads go cold fast. Responding hours later means losing jobs to whoever answered first.',
    stat: '78%',
    statLabel: 'go with the first responder',
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Manual Scheduling Bottlenecks',
    desc: 'Back-and-forth booking wastes time and creates gaps in your dispatch calendar.',
    stat: '3hrs',
    statLabel: 'avg. time lost per day',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: 'No Follow-Up on Unbooked Leads',
    desc: 'Estimates that don\'t close aren\'t dead — they\'re just not being followed up on.',
    stat: '44%',
    statLabel: 'of estimates never followed up',
  },
  {
    icon: 'ri-link-unlink-m',
    title: 'Disconnected Systems',
    desc: 'Dispatch, CRM, and marketing running separately means data gaps and missed revenue.',
    stat: '$47k',
    statLabel: 'avg. annual revenue lost',
  },
];

export default function ProblemSection() {
  return (
    <section className="bg-[#F8FAFC] py-28 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-[#F97316] text-xs font-semibold tracking-[0.2em] uppercase mb-5 font-inter">Where Revenue Disappears</p>
          <h2 className="text-5xl md:text-6xl font-black text-[#0F172A] leading-[1.05] tracking-tight mb-6 font-poppins">
            Where Home Service<br />Businesses Lose Revenue
          </h2>
          <p className="text-[#334155]/50 text-lg max-w-lg mx-auto font-inter font-light">
            The gaps in your operation are costing you more than you realize.
          </p>
        </div>

        {/* Problem Cards — Apple-style large cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {problems.map((p, i) => (
            <div
              key={i}
              className={`group relative bg-white rounded-2xl p-8 border border-[#E2E8F0]/80 hover:border-[#0F172A]/15 transition-all duration-500 cursor-default overflow-hidden ${i === 4 ? 'sm:col-span-2 lg:col-span-1' : ''}`}
            >
              {/* Subtle hover bg */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A]/0 to-[#0F172A]/0 group-hover:from-[#0F172A]/[0.02] group-hover:to-[#0F172A]/[0.04] transition-all duration-500 rounded-2xl"></div>

              <div className="relative z-10">
                {/* Stat — large, Apple-style */}
                <div className="text-4xl font-black text-[#0F172A]/8 group-hover:text-[#F97316]/15 transition-colors duration-500 font-poppins mb-4 leading-none">{p.stat}</div>

                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] mb-5 group-hover:bg-[#FEF3C7] group-hover:border-[#F97316]/20 transition-all duration-300">
                  <i className={`${p.icon} text-[#334155]/40 group-hover:text-[#F97316] text-base transition-colors duration-300`}></i>
                </div>

                <h3 className="text-[#0F172A] font-bold text-base mb-2 font-poppins">{p.title}</h3>
                <p className="text-[#334155]/50 text-sm leading-relaxed font-inter mb-4">{p.desc}</p>

                <div className="flex items-center gap-2">
                  <span className="text-[#F97316] font-bold text-sm font-poppins">{p.stat}</span>
                  <span className="text-[#334155]/40 text-xs font-inter">{p.statLabel}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Impact Statement — Apple-style dark card */}
        <div className="relative bg-[#0F172A] rounded-3xl px-10 py-14 text-center overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-[#2563EB]/15 blur-[80px] rounded-full pointer-events-none"></div>
          <div className="relative z-10">
            <p className="text-3xl md:text-4xl font-bold text-white max-w-3xl mx-auto leading-snug font-poppins">
              &ldquo;Every missed call is a lost job.<br />Every delayed response is revenue gone.&rdquo;
            </p>
            <p className="text-white/30 text-sm mt-6 font-inter">The average home service business loses $47,000+ annually to these gaps.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
